import {
  Square,
  Squish,
  Heading,
  Stack,
  Row,
  Column,
  Copy,
  Button,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams, useHistory } from 'react-router-dom';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { AddEmployeeToGroupModal } from 'src/components/enterprise/EnterpriseGroups/AddEmployeeToGroupModal';
import {
  IGroup,
  ISearchParams,
  SearchStatus,
  useDinerSearch,
  useGroupDinersSearch,
} from 'src/utilities/useSearchHooks';
import { Input } from 'src/components/Input/Input';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import { sdk } from 'src/utilities/sdk';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { getHomeBreadcrumb, buildCMUIPath, CMUIPage } from 'src/constants/urls';
import {
  ManageEmployeesModal,
  ParticipantTypes,
} from 'src/components/enterprise/set-up/steps/ManageEmployeesModal';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { EnterpriseEmptyState } from 'src/components/enterprise/EnterpriseEmptyState/EnterpriseEmptyState';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';

export const EMPLOYEE_PAGE_SIZE = 50;

interface SearchFormFields {
  dinerQuery: string;
}

export default function EnterpriseGroupDetails(): JSX.Element {
  const { corpid, groupId } = useParams<{ corpid: string; groupId: string }>();
  const { push } = useHistory();

  const { sendTealiumPageView } = useAnalytics();
  const currentQueryText = useRef(null);

  const { register, handleSubmit } = useForm<SearchFormFields>();
  const [group, setGroup] = useState<IGroup>(null);
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] =
    useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const [showManageEmployeesModal, setShowManageEmployeesModal] = useState<
    'add' | 'manage'
  >(null);

  const {
    doSearch: doEmployeeSearch,
    resultsList: diners,
    lastItemNumberFromPage,
    totalResults,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
    searchStatus,
    hasMoreResults,
    loadMoreNumber,
  } = useGroupDinersSearch(
    corpid as string,
    groupId as string,
    EMPLOYEE_PAGE_SIZE
  );

  const {
    doSearch: doCorpEmployeeSearch,
    hasNoResultsWithoutQuery: corpHasNoEmployees,
  } = useDinerSearch(corpid as string);

  useEffect(() => {
    if (corpid && groupId) {
      searchDiners();
      getGroup();
      doCorpEmployeeSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, groupId]);

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup: 'manage group_details',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      pushSuccess(`${group.name} was removed`);
      setShowRemoveConfirmationModal(false);
      push(`/corporate/enterprise/${corpid}/groups`);
    } else if (asyncTaskStatus === AsyncTaskStatus.ERROR) {
      pushError(`${group.name} was not removed. Try again.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  async function deleteGroup(): Promise<void> {
    try {
      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpid}/groups/v2/${group.id}`);
      });
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${group.name} was not removed. Try again.`,
      });
    }
  }

  async function getGroup() {
    try {
      const group = await sdk.http.get(
        `/corp-admin/${corpid}/groups/${groupId}`
      );
      setGroup(group);
    } catch (e) {
      pushErrorFromAPI(e, {
        fallbackMessage:
          'Sorry, there was an issue retrieving the group details',
      });
    }
  }

  function submitSearchInputForm(values: SearchFormFields) {
    searchDiners(values);
  }

  function searchDiners(
    { dinerQuery }: Partial<SearchFormFields> = {},
    isLoadingMore = false
  ): void {
    const searchParams: ISearchParams = {
      filter_by: ParticipantTypes.MEMBERS_ONLY,
      include_diners_not_in_tag: true,
    };

    if (dinerQuery) {
      searchParams.query = dinerQuery;
      currentQueryText.current = dinerQuery;
    } else {
      currentQueryText.current = null;
    }

    doEmployeeSearch(searchParams, isLoadingMore);
  }

  if (!group) {
    return null;
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
            {
              label: 'Groups',
              href: `/corporate/enterprise/${corpid}/groups`,
              useRouterLink: true,
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Row gutter={24}>
            <Column phone={9} gutter={24}>
              <Flex height="100%" flexDirection="column">
                <Stack y="x-sm">
                  <Heading variant="h4">{group.name}</Heading>
                </Stack>
                <Copy variant="body">{group.description}</Copy>
              </Flex>
            </Column>
            <Column phone={3} gutter={24} style={{ textAlign: 'center' }}>
              <Stack y="sm">
                <RouterButtonLink
                  href={`/corporate/enterprise/${corpid}/groups/${group.id}/edit`}
                  buttonVariant="secondary"
                  buttonSize="medium"
                  useBlockButton
                >
                  Edit group
                </RouterButtonLink>
              </Stack>
              <Button
                variant="tertiary"
                buttonSize="medium"
                block
                onClick={() => setShowRemoveConfirmationModal(true)}
              >
                Deactivate
              </Button>
            </Column>
          </Row>
        </PageSection>
      </Stack>
      <PageSection>
        {!hasNoResultsWithoutQuery && (
          <>
            <Stack y="xxx-sm">
              <Heading variant="h4">{`${getSingularOrPlural(
                totalResults,
                'Employee'
              )} in ${group.name}`}</Heading>
            </Stack>
            <Stack y="x-sm">
              <Copy variant="body">
                {totalResults > 0 ? 1 : 0} - {lastItemNumberFromPage} of{' '}
                {`${totalResults} ${getSingularOrPlural(
                  totalResults,
                  'employee'
                )}`}
              </Copy>
            </Stack>
            <Stack y="md" as="form">
              <Row gutter={24}>
                <Column gutter={24} phone={6}>
                  <Row gutter={8}>
                    <Column gutter={8} phone={8}>
                      <Input
                        ariaLabel="Search diners"
                        name="dinerQuery"
                        id="dinerQuery"
                        ref={register}
                        placeholder="Employee name"
                      />
                    </Column>
                    <Column gutter={8} phone={4}>
                      <Button
                        variant="primary"
                        block
                        buttonSize="medium"
                        onClick={handleSubmit(submitSearchInputForm)}
                      >
                        Search
                      </Button>
                    </Column>
                  </Row>
                </Column>
                <Column gutter={24} phone={6}>
                  <Row flexDirection="row-reverse" gutter={24}>
                    <Column phone={6} gutter={24}>
                      <Button
                        type="button"
                        variant="secondary"
                        block
                        buttonSize="medium"
                        onClick={() => setShowManageEmployeesModal('manage')}
                      >
                        Manage employees
                      </Button>
                    </Column>
                  </Row>
                </Column>
              </Row>
            </Stack>
          </>
        )}
        {hasNoResultsWithoutQuery ? (
          <EnterpriseEmptyState
            imageUrl="https://res.cloudinary.com/grubhub-assets/image/upload/v1605271828/corps-admin-ui/groups.svg"
            heading="Add employees to create a budget"
            buttonName="Add employees"
            onButtonClick={() =>
              corpHasNoEmployees
                ? setShowAddEmployeeModal(true)
                : setShowManageEmployeesModal('add')
            }
          />
        ) : (
          <Stack y="md">
            <Table
              isLoading={searchStatus === SearchStatus.LOADING}
              header={() => (
                <Row gutter={24}>
                  <Column gutter={24} phone={3}>
                    <Copy variant="body" color="secondaryText">
                      Employee Name
                    </Copy>
                  </Column>
                  <Column gutter={24} phone={6}>
                    <Copy variant="body" color="secondaryText">
                      Email
                    </Copy>
                  </Column>
                </Row>
              )}
              body={() =>
                totalResults > 0 ? (
                  <ul className="removeListStyles">
                    {diners.map((diner) => (
                      <Squish
                        as="li"
                        key={diner.id}
                        inset="md"
                        borderBottom="1px solid"
                        borderColor="lightLine"
                      >
                        <Row gutter={24}>
                          <Column gutter={24} phone={3}>
                            {diner.name}
                          </Column>
                          <Column gutter={24} phone={6}>
                            {diner.email}
                          </Column>

                          <Column
                            phone={3}
                            gutter={24}
                            style={{ textAlign: 'end' }}
                          >
                            <RouterLink
                              href={buildCMUIPath(CMUIPage.EMPLOYEE_DETAILS, {
                                corpId: corpid,
                                employeeId: diner.id,
                              })}
                            >
                              View
                            </RouterLink>
                          </Column>
                        </Row>
                      </Squish>
                    ))}
                  </ul>
                ) : (
                  hasNoResultsWithQuery && (
                    <NoResultsMsg
                      message={`No results matching '${currentQueryText.current}'`}
                    />
                  )
                )
              }
              showFooter={false}
            />

            {hasMoreResults && (
              <Squish inset="sm">
                <Flex alignItems="center" justifyContent="center">
                  <Button
                    isLoading={searchStatus === SearchStatus.LOADING}
                    variant="tertiary"
                    onClick={() =>
                      searchDiners(
                        {
                          dinerQuery: currentQueryText.current,
                        },
                        true
                      )
                    }
                  >
                    Load {loadMoreNumber} more
                  </Button>
                </Flex>
              </Squish>
            )}
          </Stack>
        )}
      </PageSection>
      {showManageEmployeesModal && (
        <ManageEmployeesModal
          corpId={corpid as string}
          onClose={() => setShowManageEmployeesModal(null)}
          onSuccess={searchDiners}
          group={group}
          addEmployeesMode={showManageEmployeesModal === 'add'}
        />
      )}
      {showAddEmployeeModal && (
        <AddEmployeeToGroupModal
          corpId={corpid as string}
          groupId={groupId as string}
          onSuccess={() => {
            searchDiners();
            doCorpEmployeeSearch();
            setShowAddEmployeeModal(false);
          }}
          onClose={() => setShowAddEmployeeModal(false)}
        />
      )}
      {showRemoveConfirmationModal && (
        <SimpleDialog
          id={groupId as string}
          title="Deactivate Group"
          description="Deactivating a group removes Group relationships and any ordering permissions associated to the group. Are you sure you want to do this?"
          primaryActionText="Deactivate"
          buttonsDirection="row"
          secondaryActionText="Cancel"
          onPrimaryActionClick={() => deleteGroup()}
          onSecondaryActionClick={() => setShowRemoveConfirmationModal(false)}
        />
      )}
    </Square>
  );
}
